"use client";

import { useAuth } from "@/lib/auth-context";
import { useEffect, useState, useCallback } from "react";
import { PageLoader } from "../components/page-loader";
import { useToast } from "../components/toast";
import { useConfirm } from "../components/confirm";

interface ApiKey {
  apiKeyId: string;
  keyPrefix: string;
  label: string;
  isActive: boolean;
  lastUsedAt: string | null;
  createdDate: string;
}

function KeyCard({ k, canManage, onRevoke }: { k: ApiKey; canManage: boolean; onRevoke: (id: string) => void }) {
  return (
    <div className={`flex flex-col gap-3 border-b border-white/5 px-4 py-4 last:border-b-0 ${!k.isActive ? "opacity-40" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{k.label}</p>
          <code className="mt-1 inline-block rounded-md bg-white/5 px-2 py-0.5 font-mono text-xs">{k.keyPrefix}••••••••</code>
        </div>
        {canManage && k.isActive && (
          <button
            className="shrink-0 rounded-md px-2.5 py-1 text-xs text-base-content/30 transition hover:bg-error/10 hover:text-error"
            onClick={() => onRevoke(k.apiKeyId)}
          >
            Revoke
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-base-content/30">
        {k.isActive && (
          <span>Last used: {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : "Never"}</span>
        )}
        <span>Created: {new Date(k.createdDate).toLocaleDateString()}</span>
        {!k.isActive && (
          <span className="rounded-full border border-error/20 bg-error/5 px-2 py-0.5 text-[10px] font-semibold text-error">Revoked</span>
        )}
      </div>
    </div>
  );
}

export default function ApiKeysPage() {
  const { fetchWithAuth, tenant } = useAuth();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const canManage = tenant?.role === "owner" || tenant?.role === "admin";
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [newLabel, setNewLabel] = useState("");
  const [newKey, setNewKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadKeys = useCallback(async () => {
    const res = await fetchWithAuth("/platform/api-keys");
    if (res.ok) setKeys(await res.json());
    setLoading(false);
  }, [fetchWithAuth]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await fetchWithAuth("/platform/api-keys", {
        method: "POST",
        body: JSON.stringify({ label: newLabel || "Default" }),
      });
      if (res.ok) {
        const data = await res.json();
        setNewKey(data.key);
        setNewLabel("");
        loadKeys();
      } else {
        const data = await res.json().catch(() => ({}));
        toast(data.error || "Failed to create API key", "error");
      }
    } catch {
      toast("Failed to create API key", "error");
    }
    setCreating(false);
  };

  const handleRevoke = async (id: string) => {
    const ok = await confirm({
      title: "Revoke API key",
      message: "This key will immediately stop working. This cannot be undone.",
      confirmLabel: "Revoke",
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await fetchWithAuth(`/platform/api-keys/${id}`, { method: "DELETE" });
      if (res.ok) {
        toast("API key revoked");
      } else {
        toast("Failed to revoke API key", "error");
      }
    } catch {
      toast("Failed to revoke API key", "error");
    }
    loadKeys();
  };

  const activeKeys = keys.filter((k) => k.isActive);
  const revokedKeys = keys.filter((k) => !k.isActive);

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6">
      {/* New key banner */}
      {newKey && (
        <div className="flex items-start gap-3 rounded-xl border border-success/20 bg-success/5 p-5">
          <svg className="mt-0.5 h-5 w-5 shrink-0 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">API key created — copy it now, it won&apos;t be shown again.</p>
            <code className="mt-2 block select-all break-all rounded-lg bg-black/20 p-3 font-mono text-xs">{newKey}</code>
          </div>
          <button className="shrink-0 rounded-md p-1 text-base-content/30 transition hover:text-base-content" onClick={() => setNewKey(null)}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Create */}
      {canManage && (
        <div className="rounded-xl border border-white/5 bg-base-100 p-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-base-content/30">Create API Key</p>
          <p className="mt-1 text-sm text-base-content/30">Keys authenticate your applications with the Provenance API.</p>
          <div className="mt-4 flex flex-col gap-2.5 sm:flex-row sm:gap-3">
            <input
              type="text"
              className="input input-bordered w-full bg-white/[0.02] sm:flex-1"
              placeholder="Label (e.g. Production, CI/CD, Staging)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            />
            <button
              className={`btn btn-primary w-full rounded-lg sm:w-auto sm:px-6 ${creating ? "btn-disabled" : ""}`}
              onClick={handleCreate}
            >
              {creating ? <span className="loading loading-spinner loading-sm"></span> : "Create Key"}
            </button>
          </div>
        </div>
      )}

      {/* Active keys */}
      <div className="overflow-hidden rounded-xl border border-white/5">
        <div className="flex items-center justify-between bg-base-100 px-6 py-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-base-content/30">Active Keys</p>
          <span className="text-xs text-base-content/20">{activeKeys.length}</span>
        </div>

        {activeKeys.length === 0 ? (
          <div className="bg-base-100 px-6 py-10 text-center text-sm text-base-content/20">
            No active API keys.{canManage ? " Create one above to get started." : ""}
          </div>
        ) : (
          <>
            {/* Desktop: table */}
            <div className="hidden overflow-x-auto sm:block">
              <table className="table w-full">
                <thead>
                  <tr className="border-white/5 text-xs text-base-content/30">
                    <th className="bg-base-100 font-medium">Key</th>
                    <th className="bg-base-100 font-medium">Label</th>
                    <th className="bg-base-100 font-medium">Last Used</th>
                    <th className="bg-base-100 font-medium">Created</th>
                    {canManage && <th className="bg-base-100 w-24"></th>}
                  </tr>
                </thead>
                <tbody>
                  {activeKeys.map((k) => (
                    <tr key={k.apiKeyId} className="border-white/5 transition hover:bg-white/[0.02]">
                      <td className="bg-transparent">
                        <code className="rounded-md bg-white/5 px-2 py-1 font-mono text-xs">{k.keyPrefix}••••••••</code>
                      </td>
                      <td className="bg-transparent text-sm font-medium">{k.label}</td>
                      <td className="bg-transparent text-xs text-base-content/30">
                        {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : "Never"}
                      </td>
                      <td className="bg-transparent text-xs text-base-content/30">
                        {new Date(k.createdDate).toLocaleDateString()}
                      </td>
                      {canManage && (
                        <td className="bg-transparent">
                          <button
                            className="rounded-md px-2.5 py-1 text-xs text-base-content/30 transition hover:bg-error/10 hover:text-error"
                            onClick={() => handleRevoke(k.apiKeyId)}
                          >
                            Revoke
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: card list */}
            <div className="bg-base-100 sm:hidden">
              {activeKeys.map((k) => (
                <KeyCard key={k.apiKeyId} k={k} canManage={canManage} onRevoke={handleRevoke} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Revoked keys */}
      {revokedKeys.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-white/5">
          <div className="flex items-center justify-between bg-base-100 px-6 py-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-base-content/30">Revoked Keys</p>
            <span className="text-xs text-base-content/20">{revokedKeys.length}</span>
          </div>

          {/* Desktop: table */}
          <div className="hidden overflow-x-auto sm:block">
            <table className="table w-full">
              <thead>
                <tr className="border-white/5 text-xs text-base-content/30">
                  <th className="bg-base-100 font-medium">Key</th>
                  <th className="bg-base-100 font-medium">Label</th>
                  <th className="bg-base-100 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {revokedKeys.map((k) => (
                  <tr key={k.apiKeyId} className="border-white/5 opacity-40">
                    <td className="bg-transparent"><code className="font-mono text-xs">{k.keyPrefix}••••••••</code></td>
                    <td className="bg-transparent text-sm">{k.label}</td>
                    <td className="bg-transparent text-xs">{new Date(k.createdDate).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: card list */}
          <div className="bg-base-100 sm:hidden">
            {revokedKeys.map((k) => (
              <KeyCard key={k.apiKeyId} k={k} canManage={false} onRevoke={handleRevoke} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
